import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import {
  Archive,
  ArrowLeft,
  ChevronDown,
  Download,
  Edit3,
  Forward,
  Inbox,
  Mail,
  MailOpen,
  Paperclip,
  Printer,
  RefreshCcw,
  Reply,
  ReplyAll,
  Search,
  Send,
  ShieldAlert,
  Star,
  Trash2,
  UsersRound
} from "lucide-react";
import type { ComposerSeed } from "../components/Composer";
import type { EmailAccount, EmailDetail, EmailSummary } from "../types";
import { API_BASE, api, session } from "../lib/api";
import { fileSize, formatDateTime, formatRelative, senderName } from "../lib/format";
import { Avatar, Badge, Button, EmptyState, IconButton, Loading } from "../components/ui";

type ContextMenuState = {
  x: number;
  y: number;
  message: EmailSummary;
};

function splitAddresses(value: string) {
  const addresses: string[] = [];
  let current = "";
  let quoted = false;
  let angleDepth = 0;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === '"' && value.charCodeAt(index - 1) !== 92) quoted = !quoted;
    if (!quoted && character === "<") angleDepth += 1;
    if (!quoted && character === ">") angleDepth = Math.max(0, angleDepth - 1);
    if (!quoted && angleDepth === 0 && (character === "," || character === ";")) {
      if (current.trim()) addresses.push(current.trim());
      current = "";
    } else {
      current += character;
    }
  }
  if (current.trim()) addresses.push(current.trim());
  return addresses;
}

function addressKey(value: string) {
  const bracketed = value.match(/<([^<>]+)>/);
  const email = bracketed?.[1] ?? value.match(/[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9.-]+/i)?.[0] ?? value;
  return email.trim().toLowerCase();
}

function uniqueAddresses(values: string[], excluded = new Set<string>()) {
  const seen = new Set(excluded);
  return values.filter((value) => {
    const key = addressKey(value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}


export default function MailPage({
  mode,
  accounts,
  initialMessageId,
  onCompose,
  notify,
  refreshKey,
  canViewViewerHistory
}: {
  mode: string;
  accounts: EmailAccount[];
  initialMessageId?: string;
  onCompose: (seed?: ComposerSeed) => void;
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
  refreshKey: number;
  canViewViewerHistory: boolean;
}) {
  const [messages, setMessages] = useState<EmailSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [detail, setDetail] = useState<EmailDetail | null>(null);
  const [search, setSearch] = useState("");
  const [accountId, setAccountId] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [printDetail, setPrintDetail] = useState<EmailDetail | null>(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const isArchive = mode === "Archive";
  const isTrash = mode === "Trash";
  const isSpam = mode === "Spam";

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.messages({
        folder: isArchive || isTrash ? "All" : mode,
        archived: isArchive,
        trash: isTrash,
        accountId: accountId || undefined,
        search: search || undefined,
        page,
        pageSize: 50,
        threaded: true
      });
      if (page > Math.max(1, result.totalPages)) {
        setPage(Math.max(1, result.totalPages));
        return;
      }
      setMessages(result.items);
      setTotalItems(result.totalItems);
      setTotalPages(Math.max(1, result.totalPages));
      setSelectedId((current) => {
        if (initialMessageId && result.items.some((message) => message.id === initialMessageId))
          return initialMessageId;
        if (current && result.items.some((message) => message.id === current)) return current;
        return result.items[0]?.id;
      });
    } catch (error) {
      notify("error", "Couldn’t load messages", (error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [accountId, initialMessageId, isArchive, isTrash, mode, notify, page, search]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [accountId, mode, search]);

  useEffect(() => {
    const timeout = setTimeout(loadMessages, search ? 250 : 0);
    return () => clearTimeout(timeout);
  }, [loadMessages, refreshKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    api
      .message(selectedId)
      .then((result) => {
        setDetail(result);
        setMessages((current) =>
          current.map((message) => (message.id === result.id ? { ...message, isRead: true } : message))
        );
      })
      .catch((error) => notify("error", "Couldn’t open message", error.message))
      .finally(() => setDetailLoading(false));
  }, [notify, selectedId]);

  useEffect(() => {
    setContextMenu(null);
  }, [mode]);

  useEffect(() => {
    if (!contextMenu) return;
    contextMenuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const closeOnPointer = (event: PointerEvent) => {
      if (!contextMenuRef.current?.contains(event.target as Node)) setContextMenu(null);
    };
    const closeOnKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setContextMenu(null);
    };
    const close = () => setContextMenu(null);
    window.addEventListener("pointerdown", closeOnPointer);
    window.addEventListener("keydown", closeOnKey);
    window.addEventListener("blur", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("pointerdown", closeOnPointer);
      window.removeEventListener("keydown", closeOnKey);
      window.removeEventListener("blur", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [contextMenu]);

  useEffect(() => {
    const clearPrintView = () => setPrintDetail(null);
    window.addEventListener("afterprint", clearPrintView);
    return () => window.removeEventListener("afterprint", clearPrintView);
  }, []);

  const heading = mode;
  const unread = useMemo(() => messages.filter((message) => !message.isRead).length, [messages]);

  const runBulk = async (ids: string[], action: string, successMessage?: string) => {
    if (ids.length === 0) return;
    try {
      await api.bulkMessages(ids, action);
      setSelectedIds(new Set());
      notify(
        "success",
        successMessage ?? `${ids.length} conversation${ids.length === 1 ? "" : "s"} updated`
      );
      await loadMessages();
    } catch (error) {
      notify("error", "Couldn’t update conversations", (error as Error).message);
    }
  };

  const updateState = async (id: string, values: Record<string, boolean>) => {
    const action = values.isStarred !== undefined
      ? (values.isStarred ? "star" : "unstar")
      : values.isRead !== undefined
        ? (values.isRead ? "read" : "unread")
        : values.isArchived !== undefined
          ? (values.isArchived ? "archive" : "unarchive")
          : undefined;
    if (action) await runBulk([id], action);
  };

  const reply = () => {
    if (!detail) return;
    onCompose({
      to: detail.direction === "Sent" ? detail.toAddresses : detail.fromAddress,
      subject: detail.subject.toLowerCase().startsWith("re:")
        ? detail.subject
        : `Re: ${detail.subject}`,
      accountId: detail.emailAccountId,
      bodyHtml: "<p><br></p>",
      relatedMessageId: detail.id,
      action: "reply"
    });
  };

  const replyAll = () => {
    if (!detail) return;
    const ownAddress = addressKey(detail.accountAddress);
    const toCandidates = detail.direction === "Sent"
      ? splitAddresses(detail.toAddresses)
      : [detail.fromAddress, ...splitAddresses(detail.toAddresses)];
    const to = uniqueAddresses(toCandidates, new Set([ownAddress]));
    const toKeys = new Set([ownAddress, ...to.map(addressKey)]);
    const cc = uniqueAddresses(splitAddresses(detail.ccAddresses), toKeys);
    onCompose({
      to: (to.length > 0 ? to : [detail.fromAddress]).join(", "),
      cc: cc.join(", "),
      subject: detail.subject.toLowerCase().startsWith("re:")
        ? detail.subject
        : `Re: ${detail.subject}`,
      accountId: detail.emailAccountId,
      bodyHtml: "<p><br></p>",
      relatedMessageId: detail.id,
      action: "replyall"
    });
  };

  const forward = () => {
    if (!detail) return;
    onCompose({
      subject: detail.subject.toLowerCase().startsWith("fwd:")
        ? detail.subject
        : `Fwd: ${detail.subject}`,
      accountId: detail.emailAccountId,
      bodyHtml: `<p><br></p><hr><p><strong>Forwarded message</strong><br>From: ${detail.fromAddress}<br>Date: ${formatDateTime(detail.occurredAt)}<br>Subject: ${detail.subject}</p>${detail.bodyHtml}`,
      relatedMessageId: detail.id,
      action: "forward"
    });
  };

  const permissionsFor = (message: EmailSummary | EmailDetail) =>
    accounts.find((account) => account.id === message.emailAccountId)?.permissions;

  const openContextMenu = (event: ReactMouseEvent, message: EmailSummary) => {
    event.preventDefault();
    setContextMenu({
      x: Math.max(8, Math.min(event.clientX, window.innerWidth - 232)),
      y: Math.max(8, Math.min(event.clientY, window.innerHeight - 270)),
      message
    });
  };

  const openContextMenuFromKeyboard = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    message: EmailSummary
  ) => {
    if (event.key !== "ContextMenu" && !(event.shiftKey && event.key === "F10")) return;
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    setContextMenu({
      x: Math.min(bounds.left + 36, window.innerWidth - 232),
      y: Math.min(bounds.top + 36, window.innerHeight - 270),
      message
    });
  };

  const printMessage = async (messageId: string) => {
    try {
      const printable = detail?.id === messageId ? detail : await api.message(messageId);
      setPrintDetail(printable);
      setMessages((current) =>
        current.map((message) => message.id === messageId ? { ...message, isRead: true } : message)
      );
      requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    } catch (error) {
      notify("error", "Couldn’t prepare message for printing", (error as Error).message);
    }
  };

  const downloadAttachment = async (messageId: string, attachmentId: string, name: string) => {
    try {
      const response = await fetch(`${API_BASE}/messages/${messageId}/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${session.token}` },
        credentials: "include"
      });
      if (!response.ok) throw new Error("The attachment could not be downloaded.");
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (error) {
      notify("error", "Download failed", (error as Error).message);
    }
  };

  const editDraft = () => {
    if (!detail) return;
    onCompose({
      draftId: detail.id,
      accountId: detail.emailAccountId,
      to: detail.toAddresses,
      cc: detail.ccAddresses,
      bcc: detail.bccAddresses,
      subject: detail.subject,
      bodyHtml: detail.bodyHtml
    });
  };

  const removeMessage = async (message: EmailSummary | EmailDetail | null = detail) => {
    if (!message || !window.confirm(`Move “${message.subject}” to Trash?`)) return;
    try {
      await api.bulkMessages([message.id], "trash");
      notify("success", "Conversation moved to Trash");
      if (selectedId === message.id) setSelectedId(undefined);
      await loadMessages();
    } catch (error) {
      notify("error", "Couldn’t move conversation", (error as Error).message);
    }
  };

  const handleContextMenuKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"));
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    const offset = event.key === "ArrowDown" ? 1 : -1;
    items[(current + offset + items.length) % items.length]?.focus();
  };

  const selectedPermissions = detail ? permissionsFor(detail) : undefined;
  const contextPermissions = contextMenu ? permissionsFor(contextMenu.message) : undefined;
  const selectedArray = Array.from(selectedIds);
  const allPageSelected = messages.length > 0 && messages.every((message) => selectedIds.has(message.id));
  const uniqueViewers = detail
    ? Array.from(new Map(
        [...detail.viewEvents]
          .sort((left, right) => left.viewedAt.localeCompare(right.viewedAt))
          .map((view) => [view.userId, view])
      ).values()).sort((left, right) => right.viewedAt.localeCompare(left.viewedAt))
    : [];

  return (
    <div className="mail-page">
      <section className="mail-list-pane">
        <header className="mail-pane-heading">
          <div>
            <h1>{heading}</h1>
            <span>
              {mode === "Inbox" ? `${unread} unread on this page · ${totalItems} conversations` : `${totalItems} conversations`}
            </span>
          </div>
          <IconButton label="Refresh" onClick={loadMessages}>
            <RefreshCcw size={17} />
          </IconButton>
        </header>
        <div className="mail-filter-bar">
          <label className="search-field">
            <Search size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search messages"
              aria-label="Search messages"
            />
          </label>
          <div className="select-wrap compact-select">
            <select
              value={accountId}
              aria-label="Filter by email account"
              onChange={(event) => setAccountId(event.target.value)}
            >
              <option value="">All accounts</option>
              {accounts.map((account) => (
                <option value={account.id} key={account.id}>
                  {account.displayName}
                </option>
              ))}
            </select>
            <ChevronDown size={14} />
          </div>
          <button
            type="button"
            className="mail-filter-clear"
            disabled={!search && !accountId}
            onClick={() => { setSearch(""); setAccountId(""); }}
          >
            Clear filters
          </button>
        </div>
        {messages.length > 0 && (
          <div className="mail-bulk-bar">
            <label title="Select all conversations on this page">
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={(event) => setSelectedIds(
                  event.target.checked ? new Set(messages.map((message) => message.id)) : new Set()
                )}
              />
              <span>{selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select page"}</span>
            </label>
            {selectedIds.size > 0 && (
              <div>
                <button onClick={() => void runBulk(selectedArray, "read")}>Read</button>
                <button onClick={() => void runBulk(selectedArray, "unread")}>Unread</button>
                <button onClick={() => void runBulk(selectedArray, "star")}>Favorite</button>
                {isTrash ? (
                  <button onClick={() => void runBulk(selectedArray, "restore", "Conversations restored")}>Restore</button>
                ) : isSpam ? (
                  <button onClick={() => void runBulk(selectedArray, "notspam", "Moved out of Spam")}>Not spam</button>
                ) : (
                  <>
                    <button onClick={() => void runBulk(selectedArray, isArchive ? "unarchive" : "archive")}>
                      {isArchive ? "Unarchive" : "Archive"}
                    </button>
                    <button onClick={() => void runBulk(selectedArray, "spam", "Moved to Spam")}>Spam</button>
                    <button className="danger" onClick={() => void runBulk(selectedArray, "trash", "Moved to Trash")}>Trash</button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <div className="mail-list">
          {loading ? (
            <Loading label="Syncing messages…" />
          ) : messages.length === 0 ? (
            <EmptyState
              icon={mode === "Sent" ? <Send /> : isArchive ? <Archive /> : isSpam ? <ShieldAlert /> : isTrash ? <Trash2 /> : <Inbox />}
              title={`No ${mode.toLowerCase()} messages`}
              message={search ? "Try a broader search." : "You’re all caught up here."}
            />
          ) : (
            messages.map((message) => {
              const person = senderName(
                message.direction === "Sent" ? message.toAddresses : message.fromAddress
              );
              return (
                <div className="mail-row-wrap" key={message.id}>
                  <input
                    className="mail-row-checkbox"
                    type="checkbox"
                    aria-label={`Select ${message.subject}`}
                    checked={selectedIds.has(message.id)}
                    onChange={(event) => setSelectedIds((current) => {
                      const next = new Set(current);
                      if (event.target.checked) next.add(message.id);
                      else next.delete(message.id);
                      return next;
                    })}
                  />
                <button
                  className={`mail-row ${selectedId === message.id ? "mail-row-active" : ""} ${
                    !message.isRead ? "mail-row-unread" : ""
                  }`}
                  onClick={() => setSelectedId(message.id)}
                  onContextMenu={(event) => openContextMenu(event, message)}
                  onKeyDown={(event) => openContextMenuFromKeyboard(event, message)}
                >
                  <Avatar name={person} tone={message.subject.length} />
                  <span className="mail-row-main">
                    <span className="mail-row-top">
                      <strong>{person}</strong>
                      <time>{formatRelative(message.occurredAt)}</time>
                    </span>
                    <span className="mail-row-subject">{message.subject}</span>
                    <span className="mail-row-preview">{message.preview}</span>
                    <span className="mail-row-meta">
                      <Badge tone="neutral">{message.accountName}</Badge>
                      {message.hasAttachments && <Paperclip size={13} />}
                      {message.messageCount > 1 && (
                        <span className="thread-count">{message.messageCount} messages</span>
                      )}
                      {canViewViewerHistory && message.viewCount > 0 && (
                        <span>
                          <MailOpen size={13} /> {message.viewCount}
                        </span>
                      )}
                    </span>
                  </span>
                  {message.isStarred && <Star size={15} className="star-filled" />}
                </button>
                </div>
              );
            })
          )}
        </div>
        {totalPages > 1 && (
          <nav className="mail-pagination" aria-label="Message pages">
            <button disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
              Next
            </button>
          </nav>
        )}
      </section>

      <section
        className="message-pane"
        onContextMenu={(event) => {
          if (detail) openContextMenu(event, detail);
        }}
      >
        {!selectedId ? (
          <EmptyState
            icon={<MailOpen />}
            title="Choose a conversation"
            message="Select a message from the list to read it here."
          />
        ) : detailLoading || !detail ? (
          <Loading label="Opening conversation…" />
        ) : (
          <>
            <header className="message-toolbar">
              <div className="desktop-only-back">
                <IconButton label="Back to message list">
                  <ArrowLeft size={17} />
                </IconButton>
              </div>
              <div>
                <IconButton
                  label={detail.isStarred ? "Remove from favorites" : "Add to favorites"}
                  onClick={() => updateState(detail.id, { isStarred: !detail.isStarred })}
                >
                  <Star size={18} className={detail.isStarred ? "star-filled" : ""} />
                </IconButton>
                {isTrash ? (
                  <IconButton
                    label="Restore conversation"
                    onClick={() => void runBulk([detail.id], "restore", "Conversation restored")}
                  >
                    <RefreshCcw size={18} />
                  </IconButton>
                ) : isSpam ? (
                  <IconButton
                    label="Move out of Spam"
                    onClick={() => void runBulk([detail.id], "notspam", "Moved out of Spam")}
                  >
                    <Inbox size={18} />
                  </IconButton>
                ) : selectedPermissions?.canViewArchive && (
                  <IconButton
                    label={detail.isArchived ? "Unarchive" : "Archive"}
                    onClick={() => updateState(detail.id, { isArchived: !detail.isArchived })}
                  >
                    {detail.isArchived ? <Inbox size={18} /> : <Archive size={18} />}
                  </IconButton>
                )}
                <IconButton label="Print" onClick={() => printMessage(detail.id)}>
                  <Printer size={18} />
                </IconButton>
                {!isTrash && selectedPermissions?.canDelete && (
                  <IconButton label="Move to Trash" onClick={() => removeMessage()}>
                    <Trash2 size={18} />
                  </IconButton>
                )}
              </div>
            </header>
            <article className="message-content">
              <div className="message-title-block">
                <div>
                  <Badge tone={detail.direction === "Sent" ? "violet" : "info"}>
                    {detail.direction}
                  </Badge>
                  <Badge tone="neutral">{detail.accountName}</Badge>
                </div>
                <h2>{detail.subject}</h2>
              </div>
              <div className="conversation-thread">
                {detail.conversation.map((threadMessage, index) => (
                  <details
                    className="thread-message"
                    key={threadMessage.id}
                    open={index === detail.conversation.length - 1 || detail.conversation.length <= 3}
                  >
                    <summary>
                      <Avatar
                        name={senderName(threadMessage.fromAddress)}
                        size="lg"
                        tone={threadMessage.subject.length}
                      />
                      <span>
                        <strong>{senderName(threadMessage.fromAddress)}</strong>
                        <small>
                          From {threadMessage.fromAddress} · to {threadMessage.toAddresses}
                        </small>
                      </span>
                      <span className="thread-message-meta">
                        <Badge tone="neutral">{threadMessage.folder}</Badge>
                        <time>{formatDateTime(threadMessage.occurredAt)}</time>
                        <button
                          type="button"
                          className="thread-print-button"
                          aria-label={`Print email from ${senderName(threadMessage.fromAddress)}`}
                          title="Print this email only"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void printMessage(threadMessage.id);
                          }}
                        >
                          <Printer size={14} /> Print email
                        </button>
                      </span>
                    </summary>
                    <div className="thread-message-body">
                      {threadMessage.bodyHtml ? (
                        <div className="email-body" dangerouslySetInnerHTML={{ __html: threadMessage.bodyHtml }} />
                      ) : (
                        <div className="email-body"><p>{threadMessage.bodyText}</p></div>
                      )}
                      {threadMessage.attachments.length > 0 && (
                        <div className="message-attachments">
                          <h3>
                            <Paperclip size={16} /> {threadMessage.attachments.length} attachment
                            {threadMessage.attachments.length > 1 ? "s" : ""}
                          </h3>
                          <div>
                            {threadMessage.attachments.map((attachment) => (
                              <button
                                key={attachment.id}
                                onClick={() => downloadAttachment(
                                  threadMessage.id,
                                  attachment.id,
                                  attachment.fileName
                                )}
                              >
                                <span className="attachment-doc"><Paperclip size={17} /></span>
                                <span>
                                  <strong>{attachment.fileName}</strong>
                                  <small>{fileSize(attachment.fileSize)}</small>
                                </span>
                                <Download size={16} />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </div>
              <div className="message-actions">
                {detail.direction === "Draft" ? (
                  <Button icon={<Edit3 size={17} />} onClick={editDraft}>Edit draft</Button>
                ) : (
                  <>
                    {selectedPermissions?.canReply && (
                      <Button tone="secondary" icon={<Reply size={17} />} onClick={reply}>Reply</Button>
                    )}
                    {selectedPermissions?.canReplyAll && (
                      <Button tone="secondary" icon={<ReplyAll size={17} />} onClick={replyAll}>Reply all</Button>
                    )}
                    {selectedPermissions?.canForward && (
                      <Button tone="secondary" icon={<Forward size={17} />} onClick={forward}>Forward</Button>
                    )}
                  </>
                )}
              </div>
              {canViewViewerHistory && uniqueViewers.length > 0 && (
                <div className="viewer-popover">
                  <button
                    type="button"
                    className="viewer-summary-button"
                    aria-describedby={`viewer-tooltip-${detail.id}`}
                  >
                    <UsersRound size={15} />
                    Viewed by {uniqueViewers.length} different user
                    {uniqueViewers.length === 1 ? "" : "s"}
                  </button>
                  <div
                    id={`viewer-tooltip-${detail.id}`}
                    className="viewer-tooltip"
                    role="tooltip"
                  >
                    <h3>People who opened this email</h3>
                    <div className="viewer-list">
                      {uniqueViewers.map((view) => (
                        <div key={view.userId}>
                          <Avatar name={view.userDisplayName} size="sm" />
                          <span>
                            <strong>{view.userDisplayName}</strong>
                            <small>Last opened {formatDateTime(view.viewedAt)}</small>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </article>
          </>
        )}
      </section>
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="mail-context-menu"
          role="menu"
          aria-label="Message actions"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onKeyDown={handleContextMenuKey}
        >
          <button role="menuitem" onClick={() => {
            setSelectedId(contextMenu.message.id);
            setContextMenu(null);
          }}>
            <MailOpen size={16} /> Open
          </button>
          <button role="menuitem" onClick={() => {
            void updateState(contextMenu.message.id, { isStarred: !contextMenu.message.isStarred });
            setContextMenu(null);
          }}>
            <Star size={16} className={contextMenu.message.isStarred ? "star-filled" : ""} />
            {contextMenu.message.isStarred ? "Remove from favorites" : "Add to favorites"}
          </button>
          <button role="menuitem" onClick={() => {
            void updateState(contextMenu.message.id, { isRead: !contextMenu.message.isRead });
            setContextMenu(null);
          }}>
            {contextMenu.message.isRead ? <Mail size={16} /> : <MailOpen size={16} />}
            {contextMenu.message.isRead ? "Mark as unread" : "Mark as read"}
          </button>
          {isTrash ? (
            <button role="menuitem" onClick={() => {
              void runBulk([contextMenu.message.id], "restore", "Conversation restored");
              setContextMenu(null);
            }}>
              <RefreshCcw size={16} /> Restore
            </button>
          ) : isSpam ? (
            <button role="menuitem" onClick={() => {
              void runBulk([contextMenu.message.id], "notspam", "Moved out of Spam");
              setContextMenu(null);
            }}>
              <Inbox size={16} /> Not spam
            </button>
          ) : contextPermissions?.canViewArchive && (
            <button role="menuitem" onClick={() => {
              void updateState(contextMenu.message.id, { isArchived: !contextMenu.message.isArchived });
              setContextMenu(null);
            }}>
              {contextMenu.message.isArchived ? <Inbox size={16} /> : <Archive size={16} />}
              {contextMenu.message.isArchived ? "Unarchive" : "Archive"}
            </button>
          )}
          {!isTrash && !isSpam && (
            <button role="menuitem" onClick={() => {
              void runBulk([contextMenu.message.id], "spam", "Moved to Spam");
              setContextMenu(null);
            }}>
              <ShieldAlert size={16} /> Mark as spam
            </button>
          )}
          <button role="menuitem" onClick={() => {
            void printMessage(contextMenu.message.id);
            setContextMenu(null);
          }}>
            <Printer size={16} /> Print
          </button>
          {!isTrash && contextPermissions?.canDelete && (
            <>
              <span className="context-menu-separator" />
              <button className="context-menu-danger" role="menuitem" onClick={() => {
                void removeMessage(contextMenu.message);
                setContextMenu(null);
              }}>
                <Trash2 size={16} /> Move to Trash
              </button>
            </>
          )}
        </div>
      )}
      {printDetail && (
        <article className="print-message" aria-hidden="true">
          <header>
            <h1>{printDetail.subject}</h1>
            <dl>
              <div><dt>From</dt><dd>{printDetail.fromAddress}</dd></div>
              <div><dt>To</dt><dd>{printDetail.toAddresses}</dd></div>
              {printDetail.ccAddresses && <div><dt>Cc</dt><dd>{printDetail.ccAddresses}</dd></div>}
              <div><dt>Date</dt><dd>{formatDateTime(printDetail.occurredAt)}</dd></div>
              <div><dt>Account</dt><dd>{printDetail.accountName}</dd></div>
            </dl>
          </header>
          {printDetail.bodyHtml ? (
            <section className="email-body" dangerouslySetInnerHTML={{ __html: printDetail.bodyHtml }} />
          ) : (
            <section className="email-body"><p>{printDetail.bodyText}</p></section>
          )}
          {printDetail.attachments.length > 0 && (
            <footer>
              <strong>Attachments</strong>
              <ul>
                {printDetail.attachments.map((attachment) => (
                  <li key={attachment.id}>{attachment.fileName} ({fileSize(attachment.fileSize)})</li>
                ))}
              </ul>
            </footer>
          )}
        </article>
      )}
    </div>
  );
}



